import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../hooks/useI18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '../utils/api';

interface CostCenter {
    id: string;
    name: string;
}

interface Vendor {
    id: string;
    name: string;
}

interface PaymentInstrument {
    id: string;
    name: string;
    instrument_type: string;
}

interface Currency {
    code: string;
    name: string;
}

interface AdvancedFilters {
    cost_center_id?: string;
    vendor_id?: string;
    payment_instrument_id?: string;
    date_from?: string;
    date_to?: string;
    currency?: string;
    min_amount?: string;
    max_amount?: string;
}

interface PaymentAdvancedFilterProps {
    filters: AdvancedFilters;
    onFiltersChange: (filters: AdvancedFilters) => void;
    onClear: () => void;
    hideVendorFilter?: boolean;
}

export default function PaymentAdvancedFilter({
    filters,
    onFiltersChange,
    onClear,
    hideVendorFilter = false
}: PaymentAdvancedFilterProps) {
    const { t } = useI18n();
    const [isExpanded, setIsExpanded] = useState(false);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [paymentInstruments, setPaymentInstruments] = useState<PaymentInstrument[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);

    // Load dropdown data
    useEffect(() => {
        const loadData = async () => {
            try {
                const requests = [
                    apiFetch('/api/cost-centers/?limit=100'),
                    apiFetch('/api/currencies/')
                ];

                // Only fetch vendors if the filter is not hidden
                if (!hideVendorFilter) {
                    requests.splice(1, 0, apiFetch('/api/vendors/?limit=100'));
                }

                const responses = await Promise.all(requests);
                let responseIndex = 0;

                // Cost centers
                if (responses[responseIndex].ok) {
                    const costCentersData = await responses[responseIndex].json();
                    setCostCenters(costCentersData.cost_centers || []);
                }
                responseIndex++;

                // Vendors (only if not hidden)
                if (!hideVendorFilter) {
                    if (responses[responseIndex].ok) {
                        const vendorsData = await responses[responseIndex].json();
                        setVendors(vendorsData.vendors || []);
                    }
                    responseIndex++;
                }

                // Currencies
                if (responses[responseIndex].ok) {
                    const currenciesData = await responses[responseIndex].json();
                    setCurrencies(currenciesData || []);
                }

                // Try to load payment instruments, but don't fail if endpoint doesn't exist
                try {
                    const instrumentsRes = await apiFetch('/api/payment-instruments/?limit=100');
                    if (instrumentsRes.ok) {
                        const instrumentsData = await instrumentsRes.json();
                        setPaymentInstruments(instrumentsData.payment_instruments || []);
                    }
                } catch (error) {
                    console.warn('Payment instruments endpoint not available:', error);
                    setPaymentInstruments([]);
                }
            } catch (error) {
                console.error('Error loading filter data:', error);
            }
        };

        loadData();
    }, [hideVendorFilter]);

    const handleFilterChange = (key: keyof AdvancedFilters, value: any) => {
        onFiltersChange({
            ...filters,
            [key]: value
        });
    };

    const hasActiveFilters = Object.values(filters).some(value =>
        value !== undefined && value !== '' &&
        (Array.isArray(value) ? value.length > 0 : true)
    );

    return (
        <div className="border rounded-lg bg-white">
            {/* Header */}
            <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-2">
                    {isExpanded ? (
                        <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                    )}
                    <span className="font-medium">{t('actions.advancedFilters', 'common')}</span>
                    {hasActiveFilters && (
                        <Badge variant="secondary" className="ml-2">
                            {t('status.active', 'common')}
                        </Badge>
                    )}
                </div>

            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="p-4 border-t bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-4">
                        {/* Cost Center Filter */}
                        <div className="space-y-2">
                            <Label>Cost Center</Label>
                            <Select
                                value={filters.cost_center_id || 'all'}
                                onValueChange={(value) => handleFilterChange('cost_center_id', value === 'all' ? undefined : value)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="All cost centers" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All cost centers</SelectItem>
                                    {costCenters.map((costCenter) => (
                                        <SelectItem key={costCenter.id} value={costCenter.id}>
                                            {costCenter.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Vendor Filter */}
                        {!hideVendorFilter && (
                            <div className="space-y-2">
                                <Label>Vendor</Label>
                                <Select
                                    value={filters.vendor_id || 'all'}
                                    onValueChange={(value) => handleFilterChange('vendor_id', value === 'all' ? undefined : value)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="All vendors" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All vendors</SelectItem>
                                        {vendors.map((vendor) => (
                                            <SelectItem key={vendor.id} value={vendor.id}>
                                                {vendor.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Payment Instrument Filter */}
                        {paymentInstruments.length > 0 && (
                            <div className="space-y-2">
                                <Label>Payment Method</Label>
                                <Select
                                    value={filters.payment_instrument_id || 'all'}
                                    onValueChange={(value) => handleFilterChange('payment_instrument_id', value === 'all' ? undefined : value)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="All methods" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All methods</SelectItem>
                                        {paymentInstruments.map((instrument) => (
                                            <SelectItem key={instrument.id} value={instrument.id}>
                                                {instrument.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Currency Filter */}
                        <div className="space-y-2">
                            <Label>Currency</Label>
                            <Select
                                value={filters.currency || 'all'}
                                onValueChange={(value) => handleFilterChange('currency', value === 'all' ? undefined : value)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="All currencies" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All currencies</SelectItem>
                                    {currencies.map((currency) => (
                                        <SelectItem key={currency.code} value={currency.code}>
                                            {currency.code}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Date From */}
                        <div className="space-y-2">
                            <Label>Date From</Label>
                            <Input
                                type="date"
                                value={filters.date_from || ''}
                                onChange={(e) => handleFilterChange('date_from', e.target.value || undefined)}
                            />
                        </div>

                        {/* Date To */}
                        <div className="space-y-2">
                            <Label>Date To</Label>
                            <Input
                                type="date"
                                value={filters.date_to || ''}
                                onChange={(e) => handleFilterChange('date_to', e.target.value || undefined)}
                            />
                        </div>

                        {/* Min Amount */}
                        <div className="space-y-2">
                            <Label>Min Amount</Label>
                            <Input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                value={filters.min_amount || ''}
                                onChange={(e) => handleFilterChange('min_amount', e.target.value || undefined)}
                            />
                        </div>

                        {/* Max Amount */}
                        <div className="space-y-2">
                            <Label>Max Amount</Label>
                            <Input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                value={filters.max_amount || ''}
                                onChange={(e) => handleFilterChange('max_amount', e.target.value || undefined)}
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-2 mt-4">
                        <Button onClick={() => setIsExpanded(true)}>
                            Apply Filters
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
